import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TextInput } from '@tcl-boron-prefabs/text-input';
import { isValidEmail } from '../../utils/is-valid-email';
import { ROUTES } from '../../navigation/router';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import cn from 'classnames';
import Loader from '@tcl-boron-prefabs/loader';
import { ChevronLeft, Fail } from '@tcl-boron-icons/icons';
import { TroubleLoggingIn as TroubleLoggingInFn } from '../../utils/trouble-logging-in';
import withStyles from '@tempus/component-library/utils/withStyles';
import styles from '../../styles';
import './trouble-logging-in.scss';
import '../../shared/styles.scss';

const INVALID_EMAIL_ERROR = 'INVALID_EMAIL';

function TroubleLoggingIn({ classes }) {
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasSSO, setHasSSO] = useState(false);
  const [error, setError] = useState(undefined);

  function back() {
    history.push(ROUTES.ROOT);
  }

  function updateEmail(value) {
    setEmail(value.trim());
  }

  async function submit() {
    setSubmitting(true);

    if (!isValidEmail(email)) {
      setError(INVALID_EMAIL_ERROR);
      setSubmitting(false);
      return;
    }

    setSubmitted(!submitted);

    if (submitted) {
      back();
    } else {
      const hasSSO = await TroubleLoggingInFn(email);
      if (hasSSO) setHasSSO(true);
      setSubmitting(false);
    }

    setSubmitting(false);
  }

  function renderCopy() {
    if (submitting) {
      return <div className="submitting-container" />;
    }

    if (submitted && hasSSO) {
      return (
        <>
          <h1 className={cn(typography.H1)} data-testid="submitted-header">
            {email} has SSO enabled
          </h1>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            Please handle login issues with your SSO provider.
          </p>
          <p className={cn(typography.Paragraph)}>
            Your institution can provide more details, if you still have questions then contact{' '}
            <a className={cn(typography.link)} href="mailto:support@tempus.com">
              support@tempus.com{' '}
            </a>
            for assistance.
          </p>
        </>
      );
    }

    if (submitted) {
      return (
        <>
          <h1 className={cn(typography.H1)} data-testid="submitted-header">
            Check your inbox
          </h1>
          <p className={cn(typography.Paragraph)} data-testid="submitted-paragraph">
            An email has been sent to <strong>{email}</strong>.
          </p>
          <p className={cn(typography.Paragraph)}>
            If you do not receive an email in 10 minutes, please check your spam folder, then contact{' '}
            <a className={cn(typography.link)} href="mailto:support@tempus.com">
              support@tempus.com{' '}
            </a>
            for assistance accessing your account.
          </p>
        </>
      );
    }
    return (
      <>
        <h1 className={cn(typography.H1)}>Let us help!</h1>
        <p className={cn(typography.Paragraph)}>New to Tempus? Returning user having log in issues?</p>
        <p className={cn(typography.Paragraph)}>
          Confirm the email below is associated with your account, and we will send you an email with personalized
          instructions to help you log in.
        </p>
      </>
    );
  }

  return (
    <div className="app-view-container trouble-logging-in-message" data-testid="trouble-logging-in-form">
      {submitting ? (
        <div className={cn('app-view-container', classes.loadingContainer)}>
          <Loader />
        </div>
      ) : (
        renderCopy()
      )}
      {!submitted ? (
        <TextInput
          status={error ? 'error' : 'default'}
          placeholder={'email@domain.com'}
          value={email}
          onChange={updateEmail}
          clearable
          subContent={
            error === INVALID_EMAIL_ERROR && (
              <div style={{ display: 'flex' }}>
                <Fail height="14px" width="14px" style={{ paddingRight: '2px' }} />
                Please enter a valid email
              </div>
            )
          }
        />
      ) : null}
      {!submitting && (
        <div className="custom-button send-instructions-button" onClick={submit}>
          {submitted ? 'Back to sign in' : 'Send Instructions'}
        </div>
      )}
      {!submitted ? (
        <div className="back-to-sign-in" onClick={back}>
          <ChevronLeft alt="Back to sign in" />
          <span>Back to sign in</span>
        </div>
      ) : null}
    </div>
  );
}

export default withStyles(styles)(TroubleLoggingIn);
